import { ReactRenderer } from '@tiptap/react'
import tippy, { type Instance, type Props } from 'tippy.js'
import * as React from 'react'
import {
  Suggestion,
  SuggestionKeyDownProps,
  SuggestionProps,
} from '@tiptap/suggestion'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { Node } from '@tiptap/core'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { MentionOptions } from '../_types'
import { PluginKey } from '@tiptap/pm/state'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { clsx } from 'clsx'
import {
  MentionControllerProps,
  MentionsListController,
} from '../mentions-list'
import { tabContentWrapperId } from '../../../../../../tab-content/wrapper'
import { SYSTEM_AUTHORS, productionAssetsURL } from '@basehub/shared-constants'

const MentionPluginKey = new PluginKey('MentionPluginKey')

export const Mention = Node.create<MentionOptions<PublicUser>>({
  name: 'mention',
  HTMLAttributes: {},
  deleteTriggerWithBackspace: false,
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,

  // @ts-ignore
  addOptions() {
    return {
      suggestion: {
        char: '@',
      },
    }
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }

          return {
            'data-id': attributes.id.trim(),
          }
        },
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {}
          }

          return {
            'data-label': attributes.label,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ]
  },

  addNodeView() {
    return ({
      editor,
      node,
      // getPos,
      // HTMLAttributes,
      // decorations,
      // extension,
    }) => {
      const items = this.options.suggestion?.items?.({
        query: '',
        editor: editor,
      })

      const item = items?.find((item) => item.id === node.attrs.id)
      if (!item) {
        const span = document.createElement('span')
        span.setAttribute('data-type', this.name)
        span.setAttribute('data-id', node.attrs.id)
        span.setAttribute('data-label', node.attrs.label)
        const name =
          node.attrs.label === SYSTEM_AUTHORS.START.name
            ? SYSTEM_AUTHORS.START.name.toUpperCase()
            : node.attrs.label
        span.textContent = `${this.options.suggestion!.char}${name}`
        return {
          dom: span,
        }
      }
      const name =
        item.username === SYSTEM_AUTHORS.START.name
          ? SYSTEM_AUTHORS.START.name.toUpperCase()
          : item.username
      const span = document.createElement('span')
      span.setAttribute('data-type', this.name)
      span.setAttribute('data-id', node.attrs.id)
      span.setAttribute('data-label', node.attrs.label)
      span.textContent = `${this.options.suggestion!.char}${name}`
      return {
        dom: span,
      }
    }
  },

  renderText({ node }) {
    const items = this.options.suggestion!.items!({
      query: '',
      editor: this.editor!,
    })

    const item = items.find((item) => item.id === node.attrs.id)
    if (!item) {
      return `${this.options.suggestion!.char}${
        node.attrs.label ?? node.attrs.id
      }`
    }
    const name = item.username
    return `${this.options.suggestion!.char}${name}`
  },
  renderHTML({ node }) {
    const items = this.options.suggestion?.items?.({
      query: null,
      editor: this.editor!,
    })
    const item = items?.find((item) => item.id === node.attrs.id)
    if (!item) {
      return [
        'span',
        {
          'data-type': this.name,
          'data-id': node.attrs.id,
          'data-label': node.attrs.label,
        },
        `${this.options.suggestion!.char}${node.attrs.label ?? node.attrs.id}`,
      ]
    }
    const name = item.username
    return [
      'span',
      {
        'data-type': this.name,
        'data-id': node.attrs.id,
        'data-label': node.attrs.label,
      },
      `${this.options.suggestion!.char}${name}`,
    ]
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) {
            return false
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true
              tr.insertText(
                this.options.deleteTriggerWithBackspace
                  ? ''
                  : this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              )

              return false
            }
          })

          return isMention
        }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '@',
        pluginKey: MentionPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter
          const overrideSpace = nodeAfter?.text?.startsWith(' ')

          if (overrideSpace) {
            range.to += 1
          }

          const res = editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: {
                  id: props.id.trim(),
                  label: props.username?.trim(),
                },
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run()
          window.getSelection()?.collapseToEnd()
          return res
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const type = state.schema.nodes[this.name]
          const allow = !!$from.parent.type.contentMatch.matchType(type as any)

          return allow
        },
        render: () => {
          let isOpenInternal = false
          let component:
            | ReactRenderer<
                ReactRenderer['component'],
                MentionControllerProps<PublicUser> &
                  React.RefAttributes<ReactRenderer['component']>
              >
            | undefined
          let popup: Instance<Props>

          const onExitBase = (onCloseCallback?: () => void) => {
            if (!isOpenInternal) return
            isOpenInternal = false
            popup?.destroy()
            component?.destroy()
            onCloseCallback?.()
            this.editor.view.dom.classList.remove('slash-command-open')
          }

          return {
            onStart: (props: SuggestionProps<PublicUser>) => {
              if (!props.editor.isFocused) {
                // prevent open
                // sometimes this fires without the editor being focused!
                isOpenInternal = false
                return
              }
              isOpenInternal = true
              // this.options.onOpen()
              this.editor.view.dom.classList.add('slash-command-open')
              // @ts-ignore
              component = new ReactRenderer(ForwardedUserMentionsList, {
                editor: props.editor,
                props: {
                  ...props,
                  onExit: () => {
                    onExitBase?.()
                  },
                },
              })

              const tabContent = document.querySelector<HTMLDivElement>(
                `#${tabContentWrapperId}`
              )
              if (!tabContent) {
                throw new Error('tabContent is null or undefined')
              }

              const i = tippy(tabContent, {
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
                appendTo: () => document.body,
                content: component?.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                zIndex: 71,
                moveTransition: 'transform 0.2s ease',
              })

              popup = i
            },

            onUpdate(props: SuggestionProps<PublicUser>) {
              component?.updateProps({
                ...props,
              })

              popup?.setProps({
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
              })
            },

            onKeyDown(props: SuggestionKeyDownProps) {
              if (props.event.key === 'Escape') {
                popup?.hide()

                return true
              }
              if (!component) {
                return false
              }
              return component.ref?.onKeyDown(props)
            },

            onExit() {
              popup?.destroy?.()
              component?.destroy?.()
              onExitBase?.()
            },
          }
        },

        ...this.options.suggestion,
      }),
    ]
  },
})

const renderUser: MentionControllerProps<PublicUser>['renderItem'] = ({
  isSelected,
  mouseEvents,
  className,
  item: user,
  ...rest
}) => (
  <button
    className={clsx(
      className,
      'flex p-2 text-start justify-start gap-2 border-dark-border-solid cursor-default',
      isSelected && 'bg-dark-shade-hover',
      mouseEvents && 'hover:bg-dark-shade-hover'
    )}
    data-selected={isSelected}
    {...rest}
  >
    <BaseHubAvatar
      size="medium"
      withBorder
      id={user.id}
      name={user.username ?? ''}
      forceSrc={
        user.imageUrl?.includes(productionAssetsURL) ? user.imageUrl : null
      }
      className="mt-0.5"
    />
    <div className="flex-1 flex flex-col">
      <span className="text-dark-bright text-xs font-medium">
        {user.username}
      </span>
      <span className="text-dark-faint text-xs">
        {makeFullNameFromClerkUser(user)}
      </span>
    </div>
  </button>
)

export const ForwardedUserMentionsList = React.forwardRef(
  (
    props: Omit<MentionControllerProps<PublicUser>, 'renderItem'>,
    ref: React.Ref<
      ReactRenderer<
        ReactRenderer['component'],
        MentionControllerProps<PublicUser> &
          React.RefAttributes<ReactRenderer['component']>
      >['component']
    >
  ) => {
    return (
      <MentionsListController<PublicUser>
        {...props}
        ref={ref}
        renderItem={renderUser}
      />
    )
  }
)
